import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useHomepageVersion } from '../contexts/HomepageVersionContext';
import LandingConsignment from './LandingConsignment';

const GOLD = '#B8892A';
const GOLD_L = '#D4A843';
const TEAL = '#1A6B54';
const BG = '#FAFAF8';
const BD = '#E8E0D0';
const INK = '#110E0A';
const MUTED = '#6B5E4E';

const HOUSES = [
  { code: '\u304a\u305f', name: '\u304a\u305f\u304b\u3089\u3084', jp: 'OTAKARAYA', tags: ['\u8155\u8868', '\u73e0\u5b9d', '\u540d\u5305'] },
  { code: '\u5927\u5409', name: '\u5927\u5409', jp: 'DAIKICHI', tags: ['\u8155\u8868', '\u73e0\u5b9d'] },
  { code: 'Eco', name: 'Eco Auc', jp: 'ECO AUCTION', tags: ['\u8155\u8868', '\u73e0\u5b9d', '\u540d\u5305'] },
  { code: 'RS', name: 'RS Auction', jp: 'RS AUCTION', tags: ['\u8155\u8868', '\u73e0\u5b9d'] },
  { code: 'RK', name: 'RK Auction', jp: 'RK AUCTION', tags: ['\u8155\u8868', '\u540d\u5305'] },
  { code: 'APR', name: 'APRE', jp: 'APRE AUCTION', tags: ['\u8155\u8868', '\u73e0\u5b9d'] },
  { code: 'JWA', name: 'JWA', jp: 'JAPAN WATCH', tags: ['\u8155\u8868', '\u73e0\u5b9d'] },
  { code: 'RVA', name: 'REVA', jp: 'REVA AUCTION', tags: ['\u73e0\u5b9d', '\u540d\u5305'] },
  { code: 'TL', name: 'Timeless', jp: 'TIMELESS', tags: ['\u8155\u8868'] },
  { code: 'NJ', name: 'NJ Auction', jp: 'NJ AUCTION', tags: ['\u540d\u5305', '\u73e0\u5b9d'] },
  { code: '\u5927\u9ed1', name: '\u5927\u9ed1\u5c4b', jp: 'DAIKOKUYA', tags: ['\u8155\u8868', '\u540d\u5305', '\u73e0\u5b9d'] },
];

const testimonials = [
  { name: '\u5f20\u5973\u58eb', city: '\u4e0a\u6d77', emoji: '\ud83d\udc5c', text: '\u901a\u8fc7\u4e91\u5ddd\u4e2d\u53e4\u62cd\u5230\u4e86\u4e00\u53ea\u68a6\u5bd0\u4ee5\u6c42\u7684\u7231\u9a6c\u4ed5\u94c2\u91d1\u5305\uff0c\u5168\u7a0b\u900f\u660e\uff0c\u68c0\u54c1\u62a5\u544a\u5f88\u8be6\u7ec6\uff0c\u975e\u5e38\u653e\u5fc3\u3002', item: '\u7231\u9a6c\u4ed5 Birkin 30', stars: '\u2605\u2605\u2605\u2605\u2605' },
  { name: '\u674e\u5148\u751f', city: '\u5317\u4eac', emoji: '\u231a', text: '\u7b2c\u4e00\u6b21\u62cd\u65e5\u672c\u8155\u8868\uff0c\u5ba2\u670d\u5168\u7a0b\u4e2d\u6587\u6307\u5bfc\uff0c\u6700\u7ec8\u4ee5\u5408\u7406\u4ef7\u683c\u62cd\u5230\u4e86\u767e\u8fbe\u7fe1\u4e3d\uff0c\u503c\u5f97\u4fe1\u8d56\uff01', item: '\u767e\u8fbe\u7fe1\u4e3d Nautilus', stars: '\u2605\u2605\u2605\u2605\u2605' },
  { name: '\u738b\u5973\u58eb', city: '\u6210\u90fd', emoji: '\ud83d\udc8d', text: '\u5df2\u7ecf\u901a\u8fc7\u4e91\u5ddd\u4e2d\u53e4\u62cd\u4e86\u4e03\u516b\u4ef6\u4e86\uff0c\u6bcf\u6b21\u90fd\u5f88\u987a\u5229\uff0c\u4fdd\u8bc1\u91d1\u51bb\u7ed3\u89e3\u51bb\u4e5f\u5f88\u53ca\u65f6\u3002', item: '\u5361\u5730\u4e9a Love \u6212\u6307', stars: '\u2605\u2605\u2605\u2605\u2606' },
];

function formatDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('zh-CN', { month: 'long', day: 'numeric' });
}

export default function Landing() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [liveCount, setLiveCount] = useState(null);
  const [liveLots, setLiveLots] = useState([]);
  const [marketItems, setMarketItems] = useState([]);
  const [exhibitions, setExhibitions] = useState([]);
  const homepageVersion = useHomepageVersion();

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // 全功能版数据（版本为 consignment 时不用，但 hooks 必须无条件调用）
  useEffect(() => {
    // Live lot count
    fetch('/api/lots?status=published&page_size=1')
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.total != null) setLiveCount(data.total); })
      .catch(() => {});
    // Featured lots for LIVE card
    fetch('/api/lots/featured?limit=4')
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.data) setLiveLots(data.data.slice(0, 4)); })
      .catch(() => {});
    // Hot market items
    fetch('/api/market?limit=6&sort=newest')
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.data) setMarketItems(data.data.slice(0, 6)); })
      .catch(() => {});
    // Exhibitions
    fetch('/api/exhibitions?limit=4')
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.data) setExhibitions(data.data.slice(0, 4)); })
      .catch(() => {});
  }, []);

  const px = isMobile ? '16px' : '28px';

  if (homepageVersion === 'consignment') return <LandingConsignment />;

  return (
    <div style={{ fontFamily: 'var(--sans)', background: BG, color: INK }}>


      {/* -- HERO -- */}
      <section style={{
        minHeight: isMobile ? 'auto' : '70vh',
        display: 'flex', alignItems: 'center', position: 'relative', overflow: 'hidden',
        background: 'linear-gradient(160deg, #FBF7F0 0%, #F3EBD8 40%, #EDE2CC 70%, #F5EDD8 100%)',
        paddingTop: 0,
      }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(184,137,42,.06) 1px,transparent 1px),linear-gradient(90deg,rgba(184,137,42,.06) 1px,transparent 1px)', backgroundSize: '48px 48px', pointerEvents: 'none' }} />

        <div style={{
          maxWidth: 1140, margin: '0 auto', padding: `${isMobile ? '32px' : '60px'} ${px}`,
          position: 'relative', zIndex: 2, width: '100%',
          textAlign: 'center',
        }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: 3, color: GOLD, textTransform: 'uppercase', marginBottom: 16 }}>
            <span style={{ width: 24, height: 1, background: GOLD }} />{t('brand.slogan')}<span style={{ width: 24, height: 1, background: GOLD }} />
          </div>
          <h1 style={{ fontFamily: 'var(--serif)', fontSize: isMobile ? 28 : 'clamp(36px,5vw,52px)', fontWeight: 700, color: INK, lineHeight: 1.3, letterSpacing: 2, marginBottom: 12 }}>
            <em style={{ color: GOLD, fontStyle: 'normal', borderBottom: '2px solid rgba(184,137,42,.3)' }}>{'\u73e0\u5b9d \u00b7 \u8155\u8868 \u00b7 \u540d\u5305'}</em>
          </h1>
          <p style={{ fontSize: isMobile ? 14 : 16, color: MUTED, lineHeight: 1.9, marginBottom: 32, maxWidth: 600, margin: '0 auto 32px' }}>
            {'\u4e2d\u53e4\u597d\u7269\u76f4\u8d2d \u00b7 \u73e0\u5b9d\u5c55\u4f1a\u900b\u5c55 \u00b7 \u65e5\u672c\u62cd\u5356\u4ee3\u8d2d \u00b7 \u5bc4\u5356\u6258\u7ba1'}
          </p>

          {/* Four entry buttons — 2x2 grid */}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)',
            gap: isMobile ? 10 : 14, marginBottom: 40,
            maxWidth: isMobile ? 320 : 480, margin: '0 auto 40px',
          }}>
            {[
              { path: '/market', label: '\u901b\u5e02\u573a', icon: '\ud83d\udecd\ufe0f', desc: '\u4e2d\u53e4\u597d\u7269\u76f4\u8d2d', bg: GOLD, color: '#fff', shadow: 'rgba(184,137,42,.35)' },
              { path: '/exhibitions', label: '\u770b\u5c55\u4f1a', icon: '\ud83c\udfdb\ufe0f', desc: '\u73e0\u5b9d\u5c55\u4f1a\u900b\u5c55', bg: TEAL, color: '#fff', shadow: 'rgba(26,107,84,.3)' },
              { path: '/auction-hub', label: '\u53bb\u4ee3\u62cd', icon: '\ud83d\udd28', desc: '\u65e5\u672c\u62cd\u5356\u4ee3\u8d2d', bg: INK, color: GOLD_L, shadow: 'rgba(17,14,10,.25)' },
              { path: '/consignment', label: '\u53bb\u5bc4\u5356', icon: '\ud83d\udce6', desc: '\u95f2\u7f6e\u5962\u54c1\u53d8\u73b0', bg: '#fff', color: GOLD, shadow: 'rgba(0,0,0,.06)', border: true },
            ].map(btn => (
              <button key={btn.path} onClick={() => navigate(btn.path)} style={{
                padding: isMobile ? '18px 14px' : '22px 20px',
                background: btn.bg, border: btn.border ? `1px solid ${BD}` : 'none', color: btn.color,
                borderRadius: 10, cursor: 'pointer', textAlign: 'center',
                boxShadow: `0 4px 20px ${btn.shadow}`,
                transition: 'transform .15s',
              }}
                onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'none'}
              >
                <div style={{ fontSize: isMobile ? 26 : 32, marginBottom: 6, lineHeight: 1 }}>{btn.icon}</div>
                <div style={{ fontFamily: 'var(--serif)', fontSize: isMobile ? 15 : 17, fontWeight: 700, letterSpacing: 2, marginBottom: 4 }}>{btn.label}</div>
                <div style={{ fontSize: 10, opacity: 0.7, letterSpacing: 1 }}>{btn.desc}</div>
              </button>
            ))}
          </div>

          {/* Live banner */}
          <div onClick={() => navigate('/live')} style={{
            maxWidth: isMobile ? 320 : 480, margin: '0 auto 24px',
            background: 'linear-gradient(135deg, #1A2E28, #2A4A3E)',
            borderRadius: 10, padding: isMobile ? '14px 16px' : '16px 20px',
            display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer',
            boxShadow: '0 4px 16px rgba(26,107,84,.25)',
          }}>
            <div style={{
              width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
              background: 'rgba(255,255,255,.12)', display: 'flex',
              alignItems: 'center', justifyContent: 'center', fontSize: 20,
            }}>📹</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#fff', letterSpacing: 1 }}>{t('landing.featLive')}</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,.6)', marginTop: 2 }}>{t('landing.featLiveDesc')}</div>
            </div>
            <span style={{ color: 'rgba(255,255,255,.4)', fontSize: 18 }}>›</span>
          </div>

          {/* Stats bar */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)',
            border: '1px solid rgba(184,137,42,.2)', borderRadius: 3, overflow: 'hidden',
            background: 'rgba(255,255,255,.6)', maxWidth: 640, margin: '0 auto',
          }}>
            {[
              { v: liveCount != null ? liveCount.toLocaleString() : '\u2014', l: '\u6b63\u5728\u62cd\u5356' },
              { v: `${marketItems.length > 0 ? '50+' : '\u2014'}`, l: '\u4e2d\u53e4\u597d\u7269' },
              { v: `${exhibitions.length || '\u2014'}`, l: '\u8fd1\u671f\u5c55\u4f1a' },
              { v: '11', l: '\u5408\u4f5c\u62cd\u5356\u884c' },
            ].map((s, i) => (
              <div key={s.l} style={{
                padding: isMobile ? '12px 14px' : '14px 20px', textAlign: 'center',
                borderRight: isMobile ? (i % 2 === 0 ? '1px solid rgba(184,137,42,.15)' : 'none') : (i < 3 ? '1px solid rgba(184,137,42,.15)' : 'none'),
                borderBottom: isMobile && i < 2 ? '1px solid rgba(184,137,42,.15)' : 'none',
              }}>
                <div style={{ fontFamily: 'var(--mono)', fontSize: isMobile ? 20 : 24, fontWeight: 600, color: GOLD, lineHeight: 1, marginBottom: 4 }}>{s.v}</div>
                <div style={{ fontSize: 10, color: MUTED, letterSpacing: 1, opacity: 0.7 }}>{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* -- MARKET SECTION -- */}
      <section style={{ background: BG, padding: isMobile ? '48px 0' : '72px 0' }}>
        <div style={{ maxWidth: 1140, margin: '0 auto', padding: `0 ${px}` }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: isMobile ? 20 : 32 }}>
            <div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: 3, color: GOLD, textTransform: 'uppercase', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 10 }}>
                {'\u4e2d\u53e4\u5e02\u573a'}<span style={{ width: 20, height: 1, background: GOLD }} />
              </div>
              <h2 style={{ fontFamily: 'var(--serif)', fontSize: isMobile ? 22 : 30, fontWeight: 700, color: INK, letterSpacing: 2 }}>{'\u7cbe\u9009\u597d\u7269 \u00b7 \u900f\u660e\u4ef7\u683c'}</h2>
              <p style={{ fontSize: 13, color: MUTED, marginTop: 4 }}>{'\u65e0\u9700\u6ce8\u518c\u5373\u53ef\u6d4f\u89c8\uff0c\u5e73\u53f0\u62c5\u4fdd\u00b7\u5148\u9274\u540e\u552e'}</p>
            </div>
            <button onClick={() => navigate('/market')} style={{
              padding: '8px 20px', borderRadius: 20, border: `1px solid ${BD}`, background: '#fff',
              fontSize: 13, color: GOLD, cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: 500,
            }}>{'\u67e5\u770b\u5168\u90e8 \u203a'}</button>
          </div>

          {marketItems.length > 0 ? (
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(3,1fr)', gap: isMobile ? 10 : 16 }}>
              {marketItems.map(item => {
                const photos = item.photos || [];
                const thumb = typeof photos === 'string' ? (JSON.parse(photos)[0] || '') : (photos[0] || '');
                return (
                  <button key={item.id} onClick={() => navigate(`/market/${item.id}`)} style={{
                    background: '#fff', borderRadius: 8, border: `1px solid ${BD}`,
                    overflow: 'hidden', cursor: 'pointer', textAlign: 'left', padding: 0,
                    transition: 'box-shadow .2s',
                  }}
                    onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,.08)'}
                    onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
                  >
                    <div style={{
                      height: isMobile ? 140 : 180, position: 'relative',
                      background: thumb ? `url(${thumb}) center/cover` : '#F5F0E8',
                    }}>
                      {item.item_grade && (
                        <span style={{
                          position: 'absolute', top: 8, left: 8,
                          fontSize: 10, padding: '2px 8px', borderRadius: 3,
                          background: 'rgba(0,0,0,.5)', color: '#fff',
                        }}>{item.item_grade}{'\u7ea7'}</span>
                      )}
                      {item.platform_guaranteed === 1 && (
                        <span style={{
                          position: 'absolute', top: 8, right: 8,
                          fontSize: 9, padding: '2px 6px', borderRadius: 3,
                          background: TEAL, color: '#fff',
                        }}>{'\u62c5\u4fdd'}</span>
                      )}
                    </div>
                    <div style={{ padding: '12px 14px' }}>
                      <div style={{
                        fontSize: 13, fontWeight: 500, color: INK,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>{item.item_name}</div>
                      <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>{item.brand}</div>
                      <div style={{ fontSize: 16, fontWeight: 600, color: GOLD, marginTop: 6, fontFamily: 'var(--mono)' }}>
                        {'\u00a5'}{Number(item.price_cny).toLocaleString()}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: 40, color: MUTED, fontSize: 13 }}>{'\u52a0\u8f7d\u4e2d...'}</div>
          )}
        </div>
      </section>

      {/* -- EXHIBITION SECTION -- */}
      <section style={{ background: '#F4F2EE', padding: isMobile ? '48px 0' : '72px 0' }}>
        <div style={{ maxWidth: 1140, margin: '0 auto', padding: `0 ${px}` }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: isMobile ? 20 : 32 }}>
            <div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: 3, color: TEAL, textTransform: 'uppercase', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 10 }}>
                {'\u73e0\u5b9d\u5c55\u4f1a'}<span style={{ width: 20, height: 1, background: TEAL }} />
              </div>
              <h2 style={{ fontFamily: 'var(--serif)', fontSize: isMobile ? 22 : 30, fontWeight: 700, color: INK, letterSpacing: 2 }}>{'\u7ebf\u4e0a\u540c\u4ef7 \u00b7 \u667a\u80fd\u900b\u5c55'}</h2>
              <p style={{ fontSize: 13, color: MUTED, marginTop: 4 }}>{'\u65e0\u9700\u6ce8\u518c\u5373\u53ef\u6d4f\u89c8\u5c55\u4f1a\u4fe1\u606f\uff0c\u5728\u7ebf\u9884\u7ea6\u770b\u8d27'}</p>
            </div>
            <button onClick={() => navigate('/exhibitions')} style={{
              padding: '8px 20px', borderRadius: 20, border: `1px solid ${BD}`, background: '#fff',
              fontSize: 13, color: TEAL, cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: 500,
            }}>{'\u67e5\u770b\u5168\u90e8 \u203a'}</button>
          </div>

          {exhibitions.length > 0 ? (
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2,1fr)', gap: isMobile ? 12 : 16 }}>
              {exhibitions.map(exh => {
                const STATUS = {
                  upcoming: { label: '\u5373\u5c06\u5f00\u5c55', color: '#E67E22', bg: '#FEF5E7' },
                  live: { label: '\u8fdb\u884c\u4e2d', color: TEAL, bg: '#E8F8F5' },
                  ended: { label: '\u5df2\u7ed3\u675f', color: '#95A5A6', bg: '#F2F3F4' },
                };
                const ss = STATUS[exh.status] || STATUS.ended;
                return (
                  <button key={exh.id} onClick={() => navigate(`/exhibitions/${exh.id}`)} style={{
                    width: '100%', textAlign: 'left', display: 'flex', gap: 16, padding: 18,
                    background: '#fff', borderRadius: 10, border: `1px solid ${BD}`,
                    cursor: 'pointer', transition: 'box-shadow .2s', alignItems: 'center',
                  }}
                    onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,.06)'}
                    onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
                  >
                    <div style={{
                      width: 64, height: 64, borderRadius: 12, flexShrink: 0,
                      background: exh.banner_url ? `url(${exh.banner_url}) center/cover` : 'linear-gradient(135deg, #1A2E28, #2A4A3E)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {!exh.banner_url && <span style={{ fontSize: 28 }}>{'\ud83c\udfdb'}</span>}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: 15, fontWeight: 600, color: INK, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{exh.name}</span>
                        <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 3, background: ss.bg, color: ss.color, flexShrink: 0, fontWeight: 500 }}>{ss.label}</span>
                      </div>
                      <div style={{ fontSize: 12, color: MUTED }}>
                        {'\ud83d\udccd'} {exh.venue || exh.city || '\u2014'} {'\u00b7'} {'\ud83d\udcc5'} {formatDate(exh.start_date)}
                      </div>
                      <div style={{ display: 'flex', gap: 12, fontSize: 12, color: MUTED, marginTop: 4 }}>
                        <span>{'\ud83c\udfea'} {exh.booth_count || 0} {'\u5c55\u4f4d'}</span>
                        <span>{'\ud83d\udc8e'} {exh.item_count || 0} {'\u4ef6\u5c55\u54c1'}</span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: 40, color: MUTED, fontSize: 13 }}>{'\u6682\u65e0\u5c55\u4f1a\u4fe1\u606f'}</div>
          )}
        </div>
      </section>

      {/* -- AUCTION PROXY SECTION -- */}
      <section style={{ background: INK, padding: isMobile ? '48px 0' : '72px 0' }}>
        <div style={{ maxWidth: 1140, margin: '0 auto', padding: `0 ${px}` }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 380px',
            gap: isMobile ? 28 : 48, alignItems: 'center',
          }}>
            <div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: 3, color: GOLD_L, textTransform: 'uppercase', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
                {'\u65e5\u672c\u4ee3\u62cd'}<span style={{ width: 20, height: 1, background: GOLD_L }} />
              </div>
              <h2 style={{ fontFamily: 'var(--serif)', fontSize: isMobile ? 22 : 30, fontWeight: 700, color: '#fff', letterSpacing: 2, marginBottom: 12 }}>
                {'\u76f4\u8fde 11 \u5bb6\u65e5\u672c\u62cd\u5356\u884c'}
              </h2>
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,.6)', lineHeight: 1.9, marginBottom: 24 }}>
                {'\u5b9e\u65f6\u5bf9\u63a5\u304a\u305f\u304b\u3089\u3084\u3001\u5927\u5409\u3001Eco Auc \u7b49\u65e5\u672c\u9876\u7ea7\u62cd\u5356\u884c\u3002\u81ea\u52a8\u5316\u7ade\u4ef7\u7cfb\u7edf\u7cbe\u51c6\u51fa\u4ef7\uff0c\u5168\u7a0b\u622a\u56fe\u7559\u8bc1\uff0c\u68c0\u54c1\u540e\u7075\u6d3b\u4ea4\u4ed8\u3002'}
              </p>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
                {[
                  { icon: '\ud83e\udd16', title: '\u5168\u81ea\u52a8\u7ade\u62cd', desc: '\u62cd\u5356\u7ed3\u675f\u524d\u7cbe\u51c6\u51fa\u4ef7' },
                  { icon: '\ud83d\udd12', title: '\u672a\u4e2d\u6807\u5168\u989d\u9000\u6b3e', desc: '\u4fdd\u8bc1\u91d1\u5168\u989d\u9000\u56de' },
                  { icon: '\ud83d\udd2c', title: '\u4e13\u4e1a\u68c0\u54c1\u62a5\u544a', desc: '\u4e94\u7ef4\u8bc4\u5206+\u9ad8\u6e05\u5b9e\u62cd' },
                ].map(f => (
                  <div key={f.title} style={{ flex: 1, minWidth: isMobile ? '100%' : 140 }}>
                    <div style={{ fontSize: 20, marginBottom: 6 }}>{f.icon}</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: GOLD_L, marginBottom: 2 }}>{f.title}</div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,.4)' }}>{f.desc}</div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <button onClick={() => navigate('/auction-hub')} style={{
                  padding: '12px 28px', background: GOLD, border: 'none', color: '#fff',
                  fontFamily: 'var(--serif)', fontSize: 15, fontWeight: 600, borderRadius: 3, cursor: 'pointer',
                  letterSpacing: 2, boxShadow: '0 4px 20px rgba(184,137,42,.35)',
                }}>{'\u67e5\u770b\u62cd\u54c1'}</button>
                <button onClick={() => navigate('/register')} style={{
                  padding: '12px 28px', background: 'transparent', border: `1px solid rgba(184,137,42,.4)`,
                  color: GOLD_L, borderRadius: 3, cursor: 'pointer', fontSize: 14,
                }}>{'\u6ce8\u518c\u5f00\u59cb\u59d4\u6258'}</button>
              </div>
            </div>

            {/* LIVE Card */}
            <div style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', top: -12, right: isMobile ? 0 : -16, background: 'linear-gradient(135deg,#2A6645,#3A8A5A)', borderRadius: 4, padding: '7px 12px', boxShadow: '0 4px 16px rgba(0,0,0,.15)', fontFamily: 'var(--mono)', fontSize: 10, color: '#fff', letterSpacing: 1, zIndex: 3 }}>
                {'\u2713 \u6210\u529f\u7ade\u5f97 Birkin 30'}
              </div>
              <div style={{ background: 'rgba(255,255,255,.05)', border: '1px solid rgba(184,137,42,.25)', borderRadius: 6, overflow: 'hidden', boxShadow: '0 12px 40px rgba(17,14,10,.15)', marginTop: 20 }}>
                <div style={{ padding: '10px 14px', borderBottom: '1px solid rgba(184,137,42,.12)', display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(184,137,42,.06)' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontFamily: 'var(--mono)', fontSize: 9, color: GOLD_L, letterSpacing: 2 }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: GOLD_L }} />LIVE AUCTION
                  </span>
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'rgba(255,255,255,.3)', marginLeft: 'auto', letterSpacing: 1 }}>{liveCount != null ? `${liveCount} \u4ef6\u6b63\u5728\u7ade\u62cd` : '\u6b63\u5728\u7ade\u62cd'}</span>
                </div>
                {liveLots.length > 0 ? liveLots.map((lot, i) => {
                    const imgs = (() => { try { const raw = lot.image_urls; return typeof raw === 'string' ? JSON.parse(raw) : (raw || []); } catch { return []; } })();
                    const price = lot.current_price_jpy || lot.start_price_jpy || 0;
                    const diffMs = lot.end_at ? new Date(lot.end_at) - Date.now() : null;
                    const diffH = diffMs != null && diffMs > 0 ? diffMs / 3600000 : null;
                    const urgent = diffH != null && diffH < 3;
                    const catLabel = lot.category || '';
                    return (
                      <div key={lot.id} style={{ padding: isMobile ? '12px 14px' : 16, borderBottom: i < liveLots.length - 1 ? '1px solid rgba(255,255,255,.04)' : 'none', cursor: 'pointer' }}
                        onClick={() => navigate(`/lots/${lot.id}`)}>
                        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                          <div style={{
                            width: 56, height: 56, borderRadius: 4, overflow: 'hidden', flexShrink: 0,
                            background: 'rgba(255,255,255,.06)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            {imgs[0] ? (
                              <img src={imgs[0]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              <span style={{ fontSize: 20 }}>📦</span>
                            )}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontFamily: 'var(--mono)', fontSize: 8, letterSpacing: 2, color: 'rgba(255,255,255,.3)', marginBottom: 3 }}>
                              {lot.house_name || ''} · {catLabel}
                            </div>
                            <div style={{ fontSize: 12, color: 'rgba(255,255,255,.75)', lineHeight: 1.4, marginBottom: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {lot.title_zh || lot.title_ja || ''}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
                              <div>
                                <div style={{ fontFamily: 'var(--mono)', fontSize: 14, color: GOLD_L, fontWeight: 500 }}>¥{Number(price).toLocaleString('ja-JP')}</div>
                              </div>
                              {diffMs != null && diffMs > 0 ? (
                                <div style={{ fontFamily: 'var(--mono)', fontSize: 10, padding: '3px 7px', borderRadius: 2, background: urgent ? 'rgba(155,35,53,.2)' : 'rgba(184,137,42,.1)', border: `1px solid ${urgent ? 'rgba(155,35,53,.3)' : 'rgba(184,137,42,.2)'}`, color: urgent ? '#EF9090' : GOLD_L, flexShrink: 0 }}>
                                  {diffH < 1 ? `${Math.floor(diffMs / 60000)}分` : `${Math.floor(diffH)}时${Math.floor((diffMs % 3600000) / 60000)}分`}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }) : (
                    <div style={{ padding: 24, textAlign: 'center', fontSize: 11, color: 'rgba(255,255,255,.3)' }}>加载中…</div>
                  )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* -- CONSIGNMENT SECTION -- */}
      <section style={{ background: '#F4F2EE', padding: isMobile ? '48px 0' : '72px 0' }}>
        <div style={{ maxWidth: 1140, margin: '0 auto', padding: `0 ${px}` }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: isMobile ? 28 : 48, alignItems: 'center',
          }}>
            <div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: 3, color: GOLD, textTransform: 'uppercase', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
                {'\u5bc4\u5356\u6258\u7ba1'}<span style={{ width: 20, height: 1, background: GOLD }} />
              </div>
              <h2 style={{ fontFamily: 'var(--serif)', fontSize: isMobile ? 22 : 30, fontWeight: 700, color: INK, letterSpacing: 2, marginBottom: 12 }}>
                {'\u95f2\u7f6e\u5962\u54c1 \u00b7 \u8f7b\u677e\u53d8\u73b0'}
              </h2>
              <p style={{ fontSize: 13, color: MUTED, lineHeight: 1.9, marginBottom: 24 }}>
                {'\u5c06\u60a8\u7684\u95f2\u7f6e\u8155\u8868\u3001\u73e0\u5b9d\u3001\u540d\u5305\u59d4\u6258\u5e73\u53f0\u4ee3\u5356\u3002\u4e13\u4e1a\u62cd\u7167\u3001\u7cbe\u51c6\u5b9a\u4ef7\u3001\u5e73\u53f0\u62c5\u4fdd\u4ea4\u6613\uff0c\u5356\u5bb6\u5b8c\u5168\u7701\u5fc3\u3002'}
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 24 }}>
                {[
                  { icon: '\ud83d\udcf8', title: '\u4e13\u4e1a\u62cd\u7167\u4e0a\u67b6', desc: '\u5e73\u53f0\u4e13\u4e1a\u62cd\u7167\uff0c\u591a\u89d2\u5ea6\u5c55\u793a\u5546\u54c1' },
                  { icon: '\ud83d\udcb0', title: '\u667a\u80fd\u5b9a\u4ef7', desc: '\u57fa\u4e8e\u5e02\u573a\u6570\u636e\u667a\u80fd\u5efa\u8bae\u5356\u4ef7' },
                  { icon: '\ud83d\udee1\ufe0f', title: '\u5e73\u53f0\u62c5\u4fdd', desc: '\u5148\u9274\u540e\u552e\uff0c\u4e70\u5356\u53cc\u65b9\u6709\u4fdd\u969c' },
                  { icon: '\ud83d\udcca', title: '\u900f\u660e\u7ed3\u7b97', desc: '\u4f63\u91d18%\u8d77\uff0c\u5b9a\u671f\u7ed3\u7b97\u5230\u8d26' },
                ].map(f => (
                  <div key={f.title} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    <span style={{ fontSize: 18, flexShrink: 0, marginTop: 2 }}>{f.icon}</span>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: INK, marginBottom: 2 }}>{f.title}</div>
                      <div style={{ fontSize: 11, color: MUTED, lineHeight: 1.6 }}>{f.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <button onClick={() => navigate('/consignment')} style={{
                  padding: '12px 28px', background: GOLD, border: 'none', color: '#fff',
                  fontFamily: 'var(--serif)', fontSize: 15, fontWeight: 600, borderRadius: 3, cursor: 'pointer',
                  letterSpacing: 2, boxShadow: '0 4px 20px rgba(184,137,42,.35)',
                }}>{'\u6211\u8981\u5bc4\u5356'}</button>
                <button onClick={() => navigate('/invite')} style={{
                  padding: '12px 28px', background: 'transparent', border: `1px solid ${BD}`,
                  color: MUTED, borderRadius: 3, cursor: 'pointer', fontSize: 14,
                }}>{'\u9080\u8bf7\u6709\u793c'}</button>
              </div>
            </div>

            {/* Consignment flow illustration */}
            <div style={{ background: '#fff', borderRadius: 12, border: `1px solid ${BD}`, padding: isMobile ? 20 : 28, boxShadow: '0 4px 20px rgba(0,0,0,.04)' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: INK, marginBottom: 20, textAlign: 'center', fontFamily: 'var(--serif)', letterSpacing: 1 }}>{'\u5bc4\u5356\u6d41\u7a0b'}</div>
              {[
                { step: '1', title: '\u63d0\u4ea4\u5bc4\u5356', desc: '\u62cd\u7167\u4e0a\u4f20\u5546\u54c1\u4fe1\u606f\uff0c\u586b\u5199\u671f\u671b\u4ef7\u683c', color: GOLD },
                { step: '2', title: '\u5e73\u53f0\u5ba1\u6838', desc: '\u4e13\u4e1a\u56e2\u961f\u5ba1\u6838\u5546\u54c1\uff0c\u786e\u8ba4\u4e0a\u67b6', color: TEAL },
                { step: '3', title: '\u5c55\u793a\u4e0e\u552e\u5356', desc: '\u5546\u54c1\u4e0a\u67b6\u4e2d\u53e4\u5e02\u573a\uff0c\u66dd\u5149\u7ed9\u4e70\u5bb6', color: '#E67E22' },
                { step: '4', title: '\u6210\u4ea4\u7ed3\u7b97', desc: '\u4e70\u5bb6\u4e0b\u5355\u540e\u5e73\u53f0\u7ed3\u7b97\uff0c\u8d27\u6b3e\u5230\u8d26', color: '#2980B9' },
              ].map((s, i) => (
                <div key={s.step} style={{ display: 'flex', gap: 14, alignItems: 'flex-start', marginBottom: i < 3 ? 18 : 0, position: 'relative' }}>
                  {i < 3 && <div style={{ position: 'absolute', left: 15, top: 32, width: 1, height: 'calc(100% - 12px)', background: BD }} />}
                  <div style={{
                    width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                    background: s.color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 13, fontWeight: 700, fontFamily: 'var(--mono)', position: 'relative', zIndex: 1,
                  }}>{s.step}</div>
                  <div style={{ paddingTop: 4 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: INK, marginBottom: 2 }}>{s.title}</div>
                    <div style={{ fontSize: 12, color: MUTED, lineHeight: 1.6 }}>{s.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* -- PLATFORM ADVANTAGES -- */}
      <section style={{ background: BG, padding: isMobile ? '48px 0' : '72px 0' }}>
        <div style={{ maxWidth: 1140, margin: '0 auto', padding: `0 ${px}` }}>
          <div style={{ textAlign: 'center', marginBottom: isMobile ? 28 : 48 }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: 3, color: GOLD, textTransform: 'uppercase', marginBottom: 10 }}>
              {'\u5e73\u53f0\u4f18\u52bf'}
            </div>
            <h2 style={{ fontFamily: 'var(--serif)', fontSize: isMobile ? 22 : 30, fontWeight: 700, color: INK, letterSpacing: 2 }}>{'\u4e3a\u4ec0\u4e48\u9009\u62e9\u4e91\u5ddd\u4e2d\u53e4'}</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(4,1fr)', gap: isMobile ? 12 : 20 }}>
            {[
              { icon: '\ud83d\udee1\ufe0f', title: '\u5148\u9274\u540e\u552e', desc: '\u5e73\u53f0\u62c5\u4fdd\u4ea4\u6613\uff0c\u4e13\u4e1a\u9274\u5b9a\u540e\u518d\u4ed8\u6b3e\uff0c\u6700\u957f90\u5929\u4fdd\u4fee', tag: '\u4e2d\u53e4\u5e02\u573a' },
              { icon: '\ud83c\udfdb\ufe0f', title: '\u5c55\u4f1a\u540c\u4ef7', desc: '\u7ebf\u4e0a\u7ebf\u4e0b\u540c\u4ef7\uff0c\u5728\u7ebf\u9884\u7ea6\u770b\u8d27\uff0c\u900f\u660e\u62a5\u4ef7\u65e0\u9690\u85cf', tag: '\u73e0\u5b9d\u5c55\u4f1a' },
              { icon: '\ud83e\udd16', title: '\u81ea\u52a8\u7ade\u62cd', desc: '\u76f4\u8fde11\u5bb6\u65e5\u672c\u62cd\u5356\u884c\uff0c\u7cbe\u51c6\u51fa\u4ef7\u7cfb\u7edf\uff0c\u622a\u56fe\u7559\u8bc1', tag: '\u65e5\u672c\u4ee3\u62cd' },
              { icon: '\ud83c\udfea', title: '\u5546\u5bb6\u5165\u9a7b', desc: '\u8ba4\u8bc1\u5546\u5bb6\u5bc4\u5356\u6258\u7ba1\uff0c\u4f63\u91d1\u900f\u660e\uff0c\u5b9a\u671f\u7ed3\u7b97', tag: '\u5546\u5bb6\u670d\u52a1' },
            ].map(f => (
              <div key={f.title} style={{
                background: '#fff', border: '1px solid #EAE6DF', borderRadius: 8, padding: isMobile ? '18px 16px' : 24,
                display: 'flex', flexDirection: isMobile ? 'row' : 'column', gap: isMobile ? 14 : 0,
                alignItems: isMobile ? 'flex-start' : 'stretch',
              }}>
                <div style={{ width: 44, height: 44, borderRadius: 8, background: '#F5EDD8', border: '1px solid rgba(184,137,42,.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0, ...(isMobile ? {} : { marginBottom: 14 }) }}>{f.icon}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: 'var(--serif)', fontSize: 15, fontWeight: 600, color: INK, marginBottom: 6, letterSpacing: 1 }}>{f.title}</div>
                  <div style={{ fontSize: 12, color: MUTED, lineHeight: 1.8, marginBottom: 8 }}>{f.desc}</div>
                  <span style={{ fontSize: 9, padding: '2px 8px', borderRadius: 2, background: 'rgba(184,137,42,.07)', border: '1px solid rgba(184,137,42,.15)', color: GOLD, fontFamily: 'var(--mono)', letterSpacing: 1 }}>{f.tag}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* -- AUCTION HOUSES (compact) -- */}
      <section style={{ background: '#F4F2EE', padding: isMobile ? '40px 0' : '56px 0' }}>
        <div style={{ maxWidth: 1140, margin: '0 auto', padding: `0 ${px}` }}>
          <div style={{ textAlign: 'center', marginBottom: isMobile ? 20 : 32 }}>
            <h3 style={{ fontFamily: 'var(--serif)', fontSize: isMobile ? 18 : 22, fontWeight: 700, color: INK, letterSpacing: 2 }}>{'\u76f4\u8fde\u65e5\u672c 11 \u5bb6\u9876\u7ea7\u62cd\u5356\u884c'}</h3>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: isMobile ? 8 : 12, justifyContent: 'center' }}>
            {HOUSES.map(h => (
              <div key={h.code} style={{
                background: '#fff', border: '1px solid #EAE6DF', borderRadius: 4, padding: isMobile ? '8px 14px' : '10px 18px',
                display: 'flex', gap: 10, alignItems: 'center',
              }}>
                <div style={{ width: 32, height: 32, borderRadius: 4, background: INK, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--serif)', fontSize: 11, color: GOLD_L, flexShrink: 0 }}>{h.code}</div>
                <span style={{ fontSize: 12, fontWeight: 500, color: INK }}>{h.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* -- TESTIMONIALS -- */}
      <section style={{ background: INK, padding: isMobile ? '48px 0' : '72px 0' }}>
        <div style={{ maxWidth: 1140, margin: '0 auto', padding: `0 ${px}` }}>
          <div style={{ textAlign: 'center', marginBottom: isMobile ? 24 : 40 }}>
            <h2 style={{ fontFamily: 'var(--serif)', fontSize: isMobile ? 22 : 30, fontWeight: 700, color: '#fff', letterSpacing: 2 }}>{'\u4ed6\u4eec\u7684\u4ea4\u6613\u6545\u4e8b'}</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3,1fr)', gap: isMobile ? 12 : 20 }}>
            {testimonials.map(t => (
              <div key={t.name} style={{ background: 'rgba(255,255,255,.04)', border: '1px solid rgba(184,137,42,.15)', borderRadius: 4, padding: isMobile ? '18px 16px' : 24 }}>
                <div style={{ fontFamily: 'var(--serif)', fontSize: 32, color: GOLD_L, lineHeight: 1, marginBottom: 10, opacity: 0.6 }}>"</div>
                <p style={{ fontSize: 13, color: 'rgba(255,255,255,.6)', lineHeight: 1.85, marginBottom: 14 }}>{t.text}</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, borderTop: '1px solid rgba(255,255,255,.06)', paddingTop: 12 }}>
                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#F5EDD8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, border: '1px solid rgba(184,137,42,.3)', flexShrink: 0 }}>{t.emoji}</div>
                  <div>
                    <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'rgba(255,255,255,.5)' }}>{t.name} {'\u00b7'} {t.city}</div>
                    <div style={{ fontSize: 11, color: GOLD_L, marginTop: 2 }}>{t.stars}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* -- CTA -- */}
      <section style={{ background: 'linear-gradient(135deg, #FBF7F0 0%, #F0E8D8 40%, #EDE2CC 100%)', padding: isMobile ? '56px 0' : '80px 0', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, transparent 15%, ${GOLD} 50%, transparent 85%)`, opacity: 0.3 }} />
        <div style={{ maxWidth: 680, margin: '0 auto', padding: `0 ${px}`, textAlign: 'center', position: 'relative', zIndex: 2 }}>
          <h2 style={{ fontFamily: 'var(--serif)', fontSize: isMobile ? 22 : 32, fontWeight: 700, color: INK, lineHeight: 1.3, letterSpacing: 2, marginBottom: 14 }}>{'\u5f00\u59cb\u60a8\u7684\u5962\u54c1\u4e4b\u65c5'}</h2>
          <p style={{ fontSize: 13, color: MUTED, lineHeight: 1.8, marginBottom: 28 }}>{'\u4e2d\u53e4\u5e02\u573a\u3001\u73e0\u5b9d\u5c55\u4f1a\u65e0\u9700\u6ce8\u518c\u5373\u53ef\u6d4f\u89c8\uff0c\u6ce8\u518c\u89e3\u9501\u65e5\u672c\u4ee3\u62cd\u53ca\u66f4\u591a\u529f\u80fd'}</p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/market')} style={{ padding: '14px 32px', background: GOLD, border: 'none', color: '#fff', fontFamily: 'var(--serif)', fontSize: 15, fontWeight: 600, borderRadius: 3, cursor: 'pointer', letterSpacing: 2, boxShadow: '0 4px 20px rgba(184,137,42,.35)' }}>{'\u901b\u5e02\u573a'}</button>
            <button onClick={() => navigate('/register')} style={{ padding: '14px 32px', background: 'transparent', border: `1px solid ${BD}`, color: MUTED, borderRadius: 3, cursor: 'pointer', fontSize: 14 }}>{'\u514d\u8d39\u6ce8\u518c'}</button>
          </div>
        </div>
      </section>

      {/* -- FOOTER -- */}
      <footer style={{ background: '#2A2318', padding: isMobile ? '36px 0 20px' : '48px 0 24px', borderTop: '1px solid rgba(255,255,255,.06)' }}>
        <div style={{ maxWidth: 1140, margin: '0 auto', padding: `0 ${px}` }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '240px 1fr 1fr 1fr',
            gap: isMobile ? 28 : 40,
            marginBottom: 28, paddingBottom: 28, borderBottom: '1px solid rgba(255,255,255,.06)',
          }}>
            <div>
              <div style={{ fontFamily: 'var(--serif)', fontSize: 18, color: GOLD_L, letterSpacing: 4, marginBottom: 8 }}>{'\u4e91\u5ddd\u4e2d\u53e4'}</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,.3)', lineHeight: 1.8, maxWidth: 260 }}>{'\u4e00\u7ad9\u5f0f\u5962\u4f88\u54c1\u4ea4\u6613\u5e73\u53f0\uff0c\u4e2d\u53e4\u76f4\u8d2d\u00b7\u73e0\u5b9d\u5c55\u4f1a\u00b7\u65e5\u672c\u4ee3\u62cd'}</div>
            </div>
            {[
              { title: '\u670d\u52a1', items: [
                { label: '\u4e2d\u53e4\u5e02\u573a', path: '/market' },
                { label: '\u73e0\u5b9d\u5c55\u4f1a', path: '/exhibitions' },
                { label: '\u65e5\u672c\u4ee3\u62cd', path: '/auction-hub' },
                { label: '\u9080\u8bf7\u6709\u793c', path: '/invite' },
              ]},
              { title: '\u6cd5\u5f8b', items: [
                { label: '\u670d\u52a1\u534f\u8bae' }, { label: '\u9690\u79c1\u653f\u7b56' }, { label: '\u4fdd\u8bc1\u91d1\u534f\u8bae' }, { label: '\u53cd\u6d17\u94b1\u58f0\u660e' },
              ]},
              { title: '\u8054\u7cfb\u6211\u4eec', items: [
                { label: '\ud83c\udfa7 \u5b98\u65b9\u5ba2\u670d\u4e2d\u5fc3', path: '/support' },
                { label: 'service@aucproxy.com' }, { label: '\u5de5\u4f5c\u65f6\u95f4\uff1a10:00-22:00' },
              ]},
            ].map(col => (
              <div key={col.title}>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: 2, color: 'rgba(255,255,255,.3)', textTransform: 'uppercase', marginBottom: 12 }}>{col.title}</div>
                {col.items.map(item => (
                  <div key={item.label} onClick={() => item.path && navigate(item.path)} style={{ fontSize: 12, color: 'rgba(255,255,255,.4)', marginBottom: 8, cursor: item.path ? 'pointer' : 'default' }}>{item.label}</div>
                ))}
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontFamily: 'var(--mono)', fontSize: 10, color: 'rgba(255,255,255,.2)', letterSpacing: 1, flexWrap: 'wrap', gap: 8 }}>
            <span>{'\u00a9'} {new Date().getFullYear()} {'\u4e91\u5ddd\u4e2d\u53e4 Inc.'}</span>
            {/* ICP \u5907\u6848\u53f7: \u5883\u5916\u670d\u52a1\u5668(\u963f\u91cc\u4e91\u65b0\u52a0\u5761)\u4e0d\u9002\u7528\u5de5\u4fe1\u90e8 ICP \u5907\u6848,\u65e0\u53f7\u53ef\u653e\u3002
               \u539f\u300cICP\u5907\u6848\u53f7 \u5ba1\u6838\u4e2d\u300d\u662f\u8bef\u5bfc\u6027\u5047\u5360\u4f4d(\u672a\u5728\u5907\u6848\u4e5f\u65e0\u6cd5\u5907\u6848),\u5df2\u79fb\u9664\u3002
               \u82e5\u5c06\u6765\u6539\u7528\u5927\u9646\u5df2\u5907\u6848\u57df\u540d+\u4e3b\u4f53,\u518d\u653e\u771f\u5b9e\u5907\u6848\u53f7\u5e76\u94fe\u5230 beian.miit.gov.cn\u3002 */}
          </div>
        </div>
      </footer>
    </div>
  );
}
